import {
  defineComponent,
  Fragment,
  h,
  onBeforeUnmount,
  onMounted,
  shallowRef,
  unref,
  vShow,
  watch,
  withDirectives
} from 'vue';

import { CdsTransitionFade } from '@components/transitions';
import { classNames } from '@components/utils';

import { makeLocaleProps, useLocale, useRender } from '../../../../composable';
import { CdsFunctionalButton } from '../../../CdsButton';
import { CdsStatus } from '../../../CdsStatus';
import { CdsProgress } from '../../../CdsProgress';
import { CdsLink } from '../../../CdsLink';
import { CdsTooltip } from '../../../CdsTooltip';
import { makeUploadListProps } from '../../composable';

import type { NUploader } from '../../interface';
import type { TStatus } from '../../../../composable';
import type { ComponentProps } from '@components/utils';
import type { PropType } from 'vue';

const componentName = 'CdsUploadListItem';
export default defineComponent({
  name: componentName,
  inheritAttrs: false,
  props: {
    file: {
      type: Object as PropType<NUploader.IUploadFile>,
      default: undefined
    },

    ...makeUploadListProps(),
    ...makeLocaleProps()
  },
  setup(props, { slots, attrs }) {
    const { uploader: uploaderLocale } = useLocale(props);
    const showProgress = shallowRef(false);
    const progressRafRef = shallowRef();
    const uploadingPercent = shallowRef(0);

    onMounted(() => {
      progressRafRef.value = setTimeout(() => {
        showProgress.value = true;
      }, 300);
    });

    onBeforeUnmount(() => {
      clearTimeout(unref(progressRafRef));
    });

    const mergedStatus = shallowRef(props.file?.status);

    watch(
      () => props.file?.status,
      (status) => {
        if (status !== 'removed') {
          mergedStatus.value = status;
        }
      }
    );

    useRender(() => {
      const {
        itemRender = slots.itemRender,
        items,
        file,
        showDownloadIcon,
        showRemoveIcon,
        showTooltipWithError,
        onDownload,
        onPreview,
        onRemove
      } = props;

      let statusName: TStatus = 'info';

      if (unref(mergedStatus) === 'error') {
        statusName = 'error';
      } else if (unref(mergedStatus) === 'uploading') {
        statusName = 'progress';
      } else if (unref(mergedStatus) === 'done') {
        statusName = 'success';
      }

      const statusIcon = (tooltipProps: ComponentProps<typeof CdsTooltip> = {}) =>
        h(
          'div',
          { class: classNames('cds-uploader-list-item__status'), ...tooltipProps },
          h(CdsStatus, {
            icon: true,
            status: statusName
          })
        );

      const removeIcon =
        showRemoveIcon &&
        h(CdsFunctionalButton, {
          title: uploaderLocale.removeFile,
          icon: unref(mergedStatus) === 'uploading' ? 'remove' : props.removeIcon,
          onClick: () => onRemove?.(file!)
        });
      const downloadIcon =
        showDownloadIcon &&
        file?.url &&
        unref(mergedStatus) === 'done' &&
        h(CdsFunctionalButton, {
          title: uploaderLocale.downloadFile,
          icon: props.downloadIcon,
          onClick: () => onDownload?.(file!)
        });

      const downloadOrRemove = h(
        'span',
        { key: 'download-delete', class: 'cds-uploader-list-item__actions' },
        unref(mergedStatus) === 'uploading' ? [`${unref(uploadingPercent)}%`, removeIcon] : [downloadIcon, removeIcon]
      );

      const linkProps = typeof file?.linkProps === 'string' ? JSON.parse(file?.linkProps) : file?.linkProps;

      const fileClasses = classNames('cds-uploader-list-item__name', {
        [`cds-uploader-list-item__name--${unref(mergedStatus)}`]: !!unref(mergedStatus)
      });
      const fileName = file?.url
        ? h(Fragment, {}, [
            h(
              CdsLink,
              {
                key: 'view',
                target: '_blank',
                rel: 'noopener noreferrer',
                title: file?.name,
                class: fileClasses,
                ...linkProps,
                href: file?.url,
                onClick: () => onPreview?.(file)
              },
              {
                default: () => file?.name
              }
            ),
            downloadOrRemove
          ])
        : h(Fragment, {}, [
            h(
              'span',
              {
                key: 'view',
                title: file?.name,
                class: fileClasses,
                onClick: () => onPreview?.(file!)
              },
              file?.name
            ),
            downloadOrRemove
          ]);

      const message =
        file?.response && typeof file.response === 'string'
          ? file.response
          : file?.error?.statusText || file?.error?.message || uploaderLocale.uploadError;

      const item = h('div', { class: classNames('cds-uploader-list-item__container') }, [
        unref(mergedStatus) === 'error' && showTooltipWithError
          ? h(
              CdsTooltip,
              { text: message },
              {
                activator: ({ props: tooltipProps }: { props: ComponentProps<typeof CdsTooltip> }) =>
                  statusIcon(tooltipProps)
              }
            )
          : statusIcon(),
        unref(showProgress) && fileName,
        h(
          CdsTransitionFade,
          { tag: 'div' },
          {
            default: () =>
              withDirectives(
                h(
                  'div',
                  { class: classNames('cds-uploader-list-item__progress') },
                  'percent' in file!
                    ? [
                        h(CdsProgress, {
                          absolute: false,
                          height: 2,
                          modelValue: file?.percent,
                          'onUpdate:modelValue': (value) => (uploadingPercent.value = value)
                        })
                      ]
                    : undefined
                ),
                [[vShow, unref(mergedStatus) === 'uploading']]
              )
          }
        )
      ]);

      return h(
        'div',
        {
          class: classNames('cds-uploader-list-item', {
            'cds-uploader-list-item--progress': unref(mergedStatus) === 'uploading',
            'cds-uploader-list-item--error': unref(mergedStatus) === 'error'
          })
        },
        itemRender
          ? itemRender({
              originNode: item,
              file,
              fileList: items,
              actions: {
                download: onDownload?.bind(null, file!),
                preview: onPreview?.bind(null, file!),
                remove: onRemove?.bind(null, file!)
              }
            })
          : item
      );
    });

    return {};
  }
});
